import { AddGroup } from "../components/teams/add-group";
import { EmployeeItem } from "../components/teams/employee-item";
import { useTranslation } from "react-i18next";
import { AddBranch } from "../components/branches/add-branch";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../context/toast-context";
import { Autocomplete, TextField } from "@mui/material";

import { Loading } from "../components/Loading";
import { UserService } from "../services/user-service";
import { Branch, EmployeeUser } from "../types";
import { AddEmployee } from "../components/teams/add-employee";
import { BranchService } from "../services/branch-service";
import { useInfinityScroll } from "../hooks/useInfinityScroll";
import { InfinityScrollLoader } from "../components/InfinityScrollLoader";
import { getBranchDisplayName } from "../utils/branch-display-name";

type BranchFilterOption = Branch | { branchId: null; name: string };
const ITEMS_PER_PAGE = 20;

export function Teams() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const { showToast } = useToast();
  const userService = useMemo(() => new UserService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const isFetchingRef = useRef(false);

  const branchOptions = useMemo<BranchFilterOption[]>(
    () => [
      { branchId: null, name: t("employee.filter.all") },
      ...branches,
    ],
    [branches, t]
  );

  const selectedBranchOption =
    branchOptions.find((option) => option.branchId === selectedBranchId) ?? branchOptions[0];

  const resetEmployeeList = () => {
    // No vaciar la lista acá: evita flash vacío / empleados “fantasma” ausentes
    // en otras sucursales si el refetch se aborta.
    setHasMore(true);
    setPage(0);
    setReloadToken((prev) => prev + 1);
  };

  const handleUpdateEmployees = async (
    updatedEmployees: EmployeeUser[] & {
      removedUserId?: number;
      removedBranchId?: number;
    }
  ) => {
    const removedUserId = updatedEmployees.removedUserId;
    const removedBranchId = updatedEmployees.removedBranchId;

    // Desvínculo multi-sucursal: sacar solo esa fila; el mismo user en otras sucursales queda.
    if (removedUserId != null && removedBranchId != null) {
      setEmployees((prev) =>
        prev.filter(
          (employee) =>
            !(
              employee.userId === removedUserId &&
              employee.branchId === removedBranchId
            )
        )
      );
      return;
    }

    resetEmployeeList();
  };

  const handleOnLeaveChange = (userId: number, branchId: number, onLeave: boolean) => {
    setEmployees((current) =>
      current.map((employee) =>
        employee.userId === userId && employee.branchId === branchId
          ? { ...employee, onLeave }
          : employee
      )
    );
  };

  const fetchEmployees = useCallback(
    async (pageToFetch: number, isAborted: () => boolean) => {
      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        await userService.getEmployees(
          {
            limit: ITEMS_PER_PAGE,
            offset: pageToFetch * ITEMS_PER_PAGE,
            ...(selectedBranchId == null ? {} : { branchid: selectedBranchId }),
          },
          (data) => {
            if (isAborted()) return;

            setEmployees((prev) => {
              if (pageToFetch === 0) return data.users;

              const alreadyLoaded = new Set(
                prev.map((e) => `${e.userId}-${e.branchId}`)
              );
              return [
                ...prev,
                ...data.users.filter(
                  (e) => !alreadyLoaded.has(`${e.userId}-${e.branchId}`)
                ),
              ];
            });
            setHasMore(data.users.length === ITEMS_PER_PAGE);
          },
          (errorMessage) => {
            if (!isAborted()) {
              showToast(errorMessage, "error");
            }
          }
        );
      } finally {
        isFetchingRef.current = false;
        if (!isAborted()) {
          setIsLoading(false);
        }
      }
    },
    [showToast, userService, selectedBranchId]
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchBranches = async () => {
      await branchService.getBranches(
        { limit: 100, offset: 0 },
        (data) => {
          if (!signal.aborted) {
            setBranches(data.branches.filter((branch) => branch.active));
          }
        },
        (errorMessage) => {
          if (!signal.aborted) {
            showToast(errorMessage, "error");
          }
        }
      );
    };

    fetchBranches();
    return () => {
      controller.abort();
    };
  }, [showToast, branchService]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    fetchEmployees(page, () => signal.aborted);

    return () => {
      controller.abort();
    };
  }, [fetchEmployees, page, reloadToken]);

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || isLoading || !hasMore) return;

    isFetchingRef.current = true;
    setPage((prev) => prev + 1);
  }, [isLoading, hasMore]);

  const lastEmployeeRef = useInfinityScroll({
    loadMore,
    hasMore,
    loading: isLoading,
  });

  return (
    <main className="h-full flex flex-col rounded-3xl bg-[#a0abba] p-6 overflow-hidden">
      <div className="flex mb-4 gap-2 justify-between items-center">
        <Autocomplete
          id="teams-branch-filter"
          options={branchOptions}
          value={selectedBranchOption}
          onChange={(_event, value) => {
            setSelectedBranchId(value?.branchId ?? null);
            resetEmployeeList();
          }}
          getOptionLabel={(option) => getBranchDisplayName(option)}
          isOptionEqualToValue={(option, value) => option.branchId === value.branchId}
          filterOptions={(options, state) => {
            const query = state.inputValue.toLowerCase().trim();
            if (!query) return options;
            return options.filter((option) =>
              getBranchDisplayName(option).toLowerCase().includes(query)
            );
          }}
          noOptionsText={t("dashboard.filters.no-results", "Sin resultados")}
          sx={{
            minWidth: 220,
            maxWidth: 320,
            "& .MuiInputBase-root": {
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              backgroundColor: "#fff",
              borderRadius: "999px",
              paddingLeft: "8px !important",
              minHeight: "36px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#838383",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#EF0BB8",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#EF0BB8",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("messages.filter.branch")}
              size="small"
            />
          )}
        />
        <div className="flex gap-2">
          <AddBranch />
          <AddGroup showBranches={true} />
          <AddEmployee onAddEmployees={handleUpdateEmployees} />
        </div>
      </div>

      <div className="flex flex-col flex-1 gap-2 h-full overflow-y-auto overflow-x-hidden custom-scrollbar rounded-lg p-4">
        {isLoading && page === 0 && <Loading />}
        {!isLoading && employees.length === 0 && (
          <div className="w-full h-full flex justify-center items-center text-white text-2xl">
            <p>
              {selectedBranchId == null
                ? t("employee.empty")
                : t("employee.filter.empty-branch")}
            </p>
          </div>
        )}
        {employees.length > 0 &&
          employees.map((employee) => (
            <EmployeeItem
              onUpdateEmployees={handleUpdateEmployees}
              onOnLeaveChange={handleOnLeaveChange}
              employee={employee}
              key={`${employee.userId}-${employee.branchId}`}
            />
          ))}
        {employees.length > 0 && hasMore && (
          <div ref={lastEmployeeRef} className="h-1 w-full shrink-0" />
        )}
        {isLoading && page > 0 && <InfinityScrollLoader />}
      </div>
    </main>
  );
}

